@if (load(); as load) {
<div class="flex flex-col gap-3 p-3 bg-background rounded-xl">
  <div class="sticky top-0 z-20 bg-background/95 backdrop-blur-sm -mx-3 px-3 -mt-3 pt-3 pb-3 border-b border-border flex flex-col gap-3">
    @if (viewContext() === 'details-page') {
      <div class="flex items-center -mb-2">
        <app-button variant="ghost" size="sm" icon="ArrowLeft" (click)="backClicked.emit()" class="-ml-2">
          Back to Loads
        </app-button>
      </div>
    }
    <!-- Header — Fleet-aside style -->
    <div class="flex items-center gap-3.5">
      <div class="w-12 h-12 rounded-xl bg-surface border border-border flex items-center justify-center shrink-0">
        <lucide-icon name="Package" class="w-6 h-6 text-muted"></lucide-icon>
      </div>
      <div class="flex flex-col flex-1 min-w-0">
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2 min-w-0">
            <app-heading level="h3" class="truncate">#{{ load.externalId || '—' }}</app-heading>
          </div>
          <app-badge [variant]="statusBadgeVariant()" [title]="load.status | enumLabel: 'LoadStatus'"
            [prefixIcon]="statusBadgeIcon()" size="xs"></app-badge>
        </div>
        <div class="flex items-center gap-1.5 mt-1 flex-wrap">
          @if (load.view?.customerName || load.view?.brokerName) {
          <app-text variant="muted" size="sm" class="truncate max-w-full">
            {{ load.view?.customerName || load.view?.brokerName }}
          </app-text>
          }
          @if (load.view?.pricing?.baseRate) {
          <app-badge variant="info" size="xs" [title]="(load.view?.pricing?.baseRate | currency) || ''"></app-badge>
          }
          @if (perms.canViewBilling() && load.view?.billingStatus && load.view?.billingStatus !== 'NOT_APPLICABLE') {
          <app-badge
            [variant]="billingBadgeVariant()"
            prefixIcon="Receipt"
            [title]="load.view?.billingStatus | enumLabel: 'BillingStatus'"
            size="xs"
          ></app-badge>
          }
        </div>
      </div>
    </div>

    <!-- AI Extraction in Progress Banner -->
    @if (load.processingStatus === 'processing') {
    <div
      class="p-3.5 rounded-xl border border-info/40 bg-surface flex items-center justify-between gap-3 animate-pulse"
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
    >
      <div class="flex items-center gap-2.5 min-w-0">
        <div class="w-8 h-8 rounded-lg bg-surface-high border border-info/50 flex items-center justify-center shrink-0">
          <lucide-icon name="Loader2" class="w-4 h-4 text-info animate-spin"></lucide-icon>
        </div>
        <div class="flex flex-col min-w-0">
          <app-text size="sm" weight="semibold">AI Extraction in Progress</app-text>
          <app-text size="xs" color="muted" class="truncate">
            Extracting details from {{ load.processingFileName || 'rate confirmation' }}... Details will update automatically.
          </app-text>
        </div>
      </div>
    </div>
    }

    <!-- AI Extraction Failed Banner -->
    @if (load.processingStatus === 'failed') {
    <div
      class="p-3.5 rounded-xl border border-danger/40 bg-surface flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
    >
      <div class="flex items-center gap-2.5 min-w-0">
        <div class="w-8 h-8 rounded-lg bg-surface-high border border-danger/50 flex items-center justify-center shrink-0">
          <lucide-icon name="CircleAlert" class="w-4 h-4 text-danger"></lucide-icon>
        </div>
        <div class="flex flex-col min-w-0">
          <app-text size="sm" weight="semibold" color="danger">Rate Confirmation Extraction Failed</app-text>
          <app-text size="xs" color="muted" class="truncate max-w-md">
            {{ load.processingError || 'An error occurred while parsing the document.' }}
          </app-text>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <app-button variant="outline" size="xs" prefixIcon="RefreshCw" (click)="retryExtraction(load)">Retry</app-button>
        <app-button variant="ghost" size="xs" prefixIcon="NotebookPen" (click)="dismissProcessingError(load)">Enter Details</app-button>
      </div>
    </div>
    }

    <!-- Cancellation Details Banner -->
    @if (load.status === 'CANCELLED') {
    <div
      class="p-3.5 rounded-xl border border-danger/30 bg-surface flex flex-col gap-2.5"
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
    >
      <div class="flex items-start justify-between gap-3">
        <div class="flex items-center gap-2.5">
          <div class="w-8 h-8 rounded-lg bg-surface-high border border-danger/40 flex items-center justify-center shrink-0">
            <lucide-icon name="Ban" class="w-4 h-4 text-danger"></lucide-icon>
          </div>
          <div class="flex flex-col">
            <app-text size="sm" weight="semibold" color="danger">
              {{ load.cancellation?.party === 'carrier' ? 'Cancelled by Us (Carrier)' : (load.cancellation?.party === 'upstream' ? 'Cancelled by Upstream Party' : 'Load Cancelled') }}
            </app-text>
            @if (load.cancellation?.cancelledAt) {
              <app-text size="xs" color="muted">
                {{ load.cancellation?.cancelledAt?.toDate() | date:'MMM d, y, h:mm a' }}
                @if (load.cancellation?.cancelledByName) {
                  by {{ load.cancellation?.cancelledByName }}
                }
              </app-text>
            }
          </div>
        </div>

        <!-- Invoicing / TONU Status Badge -->
        @if (load.view?.tonuDecision?.status === 'pending') {
          <app-badge variant="warning" badgeStyle="soft" title="TONU Review Pending" prefixIcon="Clock" size="xs"></app-badge>
        } @else if (load.view?.tonuDecision?.status === 'eligible') {
          <app-badge variant="success" badgeStyle="soft" [title]="'TONU: ' + ((load.view?.tonuDecision?.amount || 0) | currency)" prefixIcon="Check" size="xs"></app-badge>
        } @else {
          <app-badge variant="secondary" badgeStyle="soft" title="Not Billable" prefixIcon="MinusCircle" size="xs"></app-badge>
        }
      </div>

      @if (load.cancellation?.reason) {
        <div class="px-2.5 py-1.5 rounded-lg bg-surface-high border border-border flex flex-col gap-0.5">
          <app-text size="xs" weight="medium" color="default">
            Reason: {{ load.cancellation?.reason }}
          </app-text>
          @if (load.cancellation?.reasonDetails) {
            <app-text size="xs" color="muted" class="italic">
              &ldquo;{{ load.cancellation?.reasonDetails }}&rdquo;
            </app-text>
          }
        </div>
      }
    </div>
    }

    <!-- Action Required & Resolution Feedback Banners -->
    @if (load.actionRequiredFlag) {
    <div
      class="p-3.5 bg-surface-high border border-warning rounded-xl space-y-2"
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
    >
      <div class="flex items-start justify-between gap-2 flex-wrap">
        <div class="flex items-center gap-2 flex-wrap">
          <lucide-icon name="AlertTriangle" class="w-4 h-4 text-warning shrink-0"></lucide-icon>
          <app-text renderAs="span" size="sm" weight="bold" class="text-warning">Action Required</app-text>
          @if (load.actionRequiredCategory) {
            <span class="inline-flex items-center px-2 py-0.5 rounded-full bg-surface border border-border">
              <app-text renderAs="span" size="xs" weight="semibold" class="text-foreground">{{ load.actionRequiredCategory }}</app-text>
            </span>
          }
        </div>
        <div class="flex items-center gap-1.5 shrink-0">
          <app-button variant="ghost" size="xs" icon="Pencil" (click)="openActionRequiredModal('flag', true)" title="Edit issue details">
            Edit
          </app-button>
          <app-button variant="primary" size="xs" shape="pill" icon="CheckCircle2" (click)="openActionRequiredModal('resolve')">
            Resolve Issue
          </app-button>
        </div>
      </div>
      @if (load.actionRequiredReason) {
      <app-text size="sm" class="text-foreground leading-relaxed block">{{ load.actionRequiredReason }}</app-text>
      }
      @if (load.actionRequiredByName || formatAuditDate(load.actionRequiredAt)) {
      <app-text size="xs" color="muted" class="block">
        Flagged{{ load.actionRequiredByName ? ' by ' + load.actionRequiredByName : '' }}{{ formatAuditDate(load.actionRequiredAt) ? ' · ' + (formatAuditDate(load.actionRequiredAt) | date: 'MMM d, h:mm a') : '' }}
      </app-text>
      }
    </div>
    } @else if (load.actionResolvedFeedback && !dismissedResolvedBanner()) {
    <div
      class="p-3 bg-surface border border-border rounded-xl space-y-1.5"
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
    >
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2 min-w-0">
          <lucide-icon name="CheckCircle2" class="w-4 h-4 text-success shrink-0"></lucide-icon>
          <app-text renderAs="span" size="xs" weight="bold" class="text-success uppercase tracking-wide">Issue Resolved</app-text>
          @if (load.actionResolvedByName || formatAuditDate(load.actionResolvedAt)) {
            <app-text renderAs="span" size="xs" color="muted" class="truncate">
              {{ load.actionResolvedByName ? 'by ' + load.actionResolvedByName : '' }}{{ formatAuditDate(load.actionResolvedAt) ? ' · ' + (formatAuditDate(load.actionResolvedAt) | date: 'MMM d, h:mm a') : '' }}
            </app-text>
          }
        </div>
        <app-button variant="ghost" size="xs" [iconOnly]="true" icon="X" title="Dismiss" (click)="dismissedResolvedBanner.set(true)"></app-button>
      </div>
      <app-text size="xs" weight="medium" class="text-foreground leading-relaxed block">
        &ldquo;{{ load.actionResolvedFeedback }}&rdquo;
      </app-text>
      @if (load.actionResolvedPreviousReason) {
        <app-text size="xs" color="muted" class="block truncate" [title]="load.actionResolvedPreviousReason">
          Previous issue: {{ load.actionResolvedPreviousReason }}
        </app-text>
      }
    </div>
    }

    <!-- Draft Load Validation Checklist Banner -->
    @if (load.status === 'DRAFT' && !draftValidation().isValid) {
    <div
      class="p-3.5 rounded-xl border border-warning/30 bg-surface-high flex flex-col gap-3"
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
    >
      <div class="flex items-start gap-2.5">
        <lucide-icon name="TriangleAlert" class="w-5 h-5 text-warning shrink-0 mt-0.5"></lucide-icon>
        <div class="flex flex-col">
          <app-text size="sm" weight="semibold" class="text-warning">
            Action Required to Confirm Load
          </app-text>
          <app-text size="xs" variant="muted">
            Complete the following items before this load can be verified and confirmed:
          </app-text>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        @for (error of draftValidation().errors; track error.field + error.message) {
        <div
          class="flex items-start justify-between gap-3 px-3 py-2 rounded-lg bg-surface border border-border"
          animate.enter="hv-item-enter"
          animate.leave="hv-item-leave"
        >
          <div class="flex items-start gap-2 min-w-0 flex-1">
            <lucide-icon name="CircleAlert" class="w-3.5 h-3.5 text-warning shrink-0 mt-0.5"></lucide-icon>
            <app-text size="xs" weight="medium" class="text-foreground leading-relaxed break-words">{{ error.message }}</app-text>
          </div>
          <app-button
            variant="outline"
            size="xs"
            (click)="fixValidationError(error.field)"
            class="shrink-0"
          >
            Fix
          </app-button>
        </div>
        }
      </div>
    </div>
    }

    <!-- Action Hierarchy: Primary Lifecycle CTA + Secondary Load Tools -->
    <div class="flex flex-col gap-2">
      @if (loadState().showMainAction) {
        <app-button
          variant="primary"
          size="md"
          [icon]="loadState().mainActionIcon"
          [disabled]="isLoading() || !!loadState().mainActionDisabled"
          [title]="loadState().mainActionTooltip || ''"
          (click)="executeMainAction()"
          class="w-full"
        >
          {{ loadState().mainActionText }}
        </app-button>
      }

      <div
        class="grid gap-2"
        [class.grid-cols-2]="!load.actionRequiredFlag"
        [class.grid-cols-1]="!!load.actionRequiredFlag"
      >
        <div class="@container relative min-w-0">
          <app-button
            [variant]="stickyBoardOpen() ? 'outline' : 'secondary'"
            [class]="stickyBoardOpen() ? 'w-full bg-primary-low-contrast' : 'w-full'"
            size="sm"
            prefixIcon="StickyNote"
            [suffixIcon]="stickyBoardOpen() ? 'ChevronUp' : 'ChevronDown'"
            (click)="toggleStickyBoard()"
            [title]="
              stickyBoardOpen()
                ? 'Hide Sticky Notes (Esc)'
                : stickyNotes().length > 0
                  ? 'Open Sticky Notes (' + stickyNotes().length + ')'
                  : 'Open personal & team sticky notes'
            "
          >
            <span class="@[175px]:hidden">Stickies</span>
            <span class="hidden @[175px]:inline">Sticky Notes</span>
            @if (stickyNotes().length > 0) {
              <span class="sr-only"> ({{ stickyNotes().length }})</span>
            }
          </app-button>

          @if (stickyNotes().length > 0) {
            <span
              class="sticky-corner-badge pointer-events-none absolute -top-1.5 -right-1.5 z-10 inline-flex items-center justify-center min-w-[1.25rem] h-[1.25rem] px-1 rounded-full bg-primary border-2 border-surface shadow-sm"
              animate.enter="hv-chip-enter"
              animate.leave="hv-chip-leave"
              aria-hidden="true"
            >
              @for (count of [stickyNotes().length]; track count) {
                <app-text
                  renderAs="span"
                  size="xs"
                  weight="bold"
                  class="text-primary-foreground !text-[10px] !leading-none"
                  animate.enter="hv-pop"
                >
                  {{ count > 99 ? '99+' : count }}
                </app-text>
              }
            </span>
          }
        </div>

        @if (!load.actionRequiredFlag) {
          <div class="@container min-w-0">
            <app-button
              variant="secondary"
              size="sm"
              icon="Flag"
              (click)="toggleActionRequired()"
              title="Flag this load when action is required"
              class="w-full"
            >
              <span class="@[140px]:hidden">Flag</span>
              <span class="hidden @[140px]:inline">Flag Issue</span>
            </app-button>
          </div>
        }
      </div>
    </div>

    <!-- Anchored Floating Sticky Scratchpad Popover (drops down right beneath sticky header at any scroll depth) -->
    @if (activeCompanyId()) {
      <div
        class="absolute left-0 right-0 top-full pt-2.5 px-3 z-30"
        [class.pointer-events-none]="!stickyBoardOpen()"
      >
        <app-load-sticky-notes
          [loadId]="load.id"
          [companyId]="activeCompanyId()"
          [stops]="load.stops"
          [notes]="stickyNotes()"
          [isOpen]="stickyBoardOpen()"
          [caretAlign]="load.actionRequiredFlag ? 'center' : 'left'"
          [activeStopFilter]="stickyStopFilter()"
          (openBoard)="stickyBoardOpen.set(true)"
          (closeBoard)="toggleStickyBoard()"
          (clearStopFilter)="stickyStopFilter.set(null)"
        />
      </div>
    }
  </div>

  <!-- Zero-clutter 1-line pinned reminder strip at top of load when popover is closed -->
  @if (activeCompanyId()) {
    <app-load-sticky-notes
      [loadId]="load.id"
      [companyId]="activeCompanyId()"
      [stops]="load.stops"
      [notes]="stickyNotes()"
      [isOpen]="stickyBoardOpen()"
      [pinnedStripOnly]="true"
      [activeStopFilter]="stickyStopFilter()"
      (openBoard)="stickyBoardOpen.set(true)"
      (closeBoard)="toggleStickyBoard()"
      (clearStopFilter)="stickyStopFilter.set(null)"
    />
  }

  <!-- Collapsible Sections Toolbar (anchored directly to the detail cards it controls) -->
  <div class="flex items-center justify-between px-1 pt-0.5 -mb-1">
    <app-text size="xs" weight="semibold" color="muted" class="uppercase tracking-wider">
      Details
    </app-text>
    <app-button
      variant="ghost"
      size="xs"
      [icon]="allCollapsed() ? 'UnfoldVertical' : 'FoldVertical'"
      (click)="toggleAllSections()"
      [title]="allCollapsed() ? 'Expand all detail sections' : 'Collapse all detail sections'"
    >
      {{ allCollapsed() ? 'Expand all' : 'Collapse all' }}
    </app-button>
  </div>

  <!-- Section 1: Booking Details -->
  <app-card headerText="Booking Details" size="sm" icon="Banknote" [editable]="true"
    (edit)="openEditModal('assignments', load)" editBtnTooltip="Change Booking Details" [collapsible]="true"
    [(collapsed)]="bookingCollapsed">
    <!-- Collapsed Summary -->
    <div card-summary class="flex justify-between items-center text-sm">
      <div class="flex flex-col">
        <app-text size="sm" color="muted">Booked At:</app-text>
        <app-text size="sm" weight="semibold">{{ load.view?.pricing?.baseRate | currency }}</app-text>
      </div>
      @if (load.view?.pricing?.baseRate && load.forwardedToOwnerOp) {
      <div class="flex items-center justify-between">
        <app-text size="sm" color="muted">Forwarded At:</app-text>
        <app-text size="sm" weight="semibold">{{ load.view?.pricing?.baseRate | currency }}</app-text>
      </div>
      }
    </div>
    <div class="flex justify-between items-center py-3 mb-4 border-b border-border">
      <div class="flex flex-col">
        <app-text size="sm" color="muted">Booked At</app-text>
        <app-heading level="h2" class="font-bold!">{{ load.view?.pricing?.baseRate | currency }}</app-heading>
      </div>

      @if (load.view?.pricing?.baseRate && load.forwardedToOwnerOp) {
      <div>
        <app-text size="sm" color="muted">Forwarded At</app-text>
        <app-heading level="h2" class="font-bold!">{{ (load.view?.pricing?.baseRate | currency) || "--" }}</app-heading>
      </div>
      }
    </div>
    <div class="space-y-3 text-sm">
      <div class="flex justify-between">
        <app-text color="muted">Booked On</app-text>
        <app-text size="sm" weight="medium">{{ load.view?.pricing?.bookedAt ? (load.view?.pricing?.bookedAt?.toDate() | date:'MMM d, yyyy') : '--' }}</app-text>
      </div>
      @if (bookedFromParty(); as party) {
      <div class="flex items-center justify-between">
        <app-text color="muted">Booked From</app-text>
        <div class="flex items-center gap-2">
          <app-text weight="medium">{{ party.name | titlecase }}</app-text>
          <app-badge variant="secondary" badgeStyle="outline" size="sm">
            {{ party.role === 'direct_shipper' ? 'Shipper' : (party.role | titlecase) }}
          </app-badge>
        </div>
      </div>
      }
      <div class="flex items-center justify-between">
        <app-text color="muted">Carrier of Record</app-text>
        @if (isCarrierForwarded()) {
        <div class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md border-gradient-peacock bg-surface-high"
             [title]="carrierOfRecordTooltip()">
          <lucide-icon name="ArrowRightLeft" size="12" class="text-forwarded shrink-0"></lucide-icon>
          <app-text size="sm" weight="semibold" class="text-foreground">
            {{ carrierOfRecordName() | titlecase }}
          </app-text>
          <app-badge variant="secondary" badgeStyle="outline" size="sm" class="ml-0.5">Carrier</app-badge>
        </div>
        } @else {
        <div class="flex items-center gap-2">
          <app-text weight="medium">{{ carrierOfRecordName() | titlecase }}</app-text>
          <app-badge variant="secondary" badgeStyle="outline" size="sm">Carrier</app-badge>
        </div>
        }
      </div>
      @if (operatedByParty(); as op) {
      <div class="flex items-center justify-between">
        <app-text color="muted">Operated By</app-text>
        <div class="flex items-center gap-2">
          <app-text weight="medium">{{ op.name | titlecase }}</app-text>
          <app-badge variant="secondary" badgeStyle="outline" size="sm">
            {{ op.role === 'owner_operator' ? 'Owner Operator' : (op.role | titlecase) }}
          </app-badge>
        </div>
      </div>
      }
    </div>
  </app-card>

  <!-- Section 2: Documents -->
  <app-card headerText="Documents" size="sm" icon="Files" [collapsible]="true" [(collapsed)]="documentsCollapsed">
    <!-- Collapsed Summary -->
    <div card-summary class="flex justify-between items-center text-sm">
      <app-text color="muted" size="xs">Documents</app-text>
      <app-text weight="medium" size="xs">{{ availableDocumentsCount() }} available</app-text>
    </div>

    <div class="space-y-4">
      <!-- Financial Documents (Rate Con, Invoice) -->
      @if (load.view?.documents?.rateConfirmation || load.view?.documents?.invoice) {
      <div animate.enter="hv-item-enter" animate.leave="hv-item-leave">
        <app-text size="xs" color="muted" weight="medium" class="uppercase tracking-wider block mb-2">
          Financial Documents
        </app-text>
        <div class="flex flex-wrap gap-2">
          @if (rateConfirmationOptions().length > 1) {
          <app-dropdown
            [triggerLabel]="'Rate Con (' + rateConfirmationOptions().length + ' versions)'"
            triggerIcon="FileText"
            variant="secondary"
            size="sm"
            [options]="rateConfirmationOptions()"
            (selected)="viewDocumentUrl($event.value, $event.label, 'rateConfirmation')"
          ></app-dropdown>
          } @else if (load.view?.documents?.rateConfirmation) {
          <app-button variant="secondary" size="sm" prefixIcon="FileText"
            (click)="viewDocument('rateConfirmation', 'Rate Confirmation')">
            Rate Con
          </app-button>
          }
          @if (load.view?.documents?.invoice) {
          <app-button variant="secondary" size="sm" prefixIcon="Receipt"
            (click)="viewDocument('invoice', 'Invoice')">
            Invoice
          </app-button>
          }
        </div>
      </div>
      }

      <!-- Operational Dispatch Sheets -->
      <div [ngClass]="{'pt-3 border-t border-border': load.view?.documents?.rateConfirmation || load.view?.documents?.invoice}">
        <div class="flex items-center justify-between mb-2.5">
          <app-text size="xs" color="muted" weight="medium" class="uppercase tracking-wider">
            Dispatch Sheets
          </app-text>
          @if (segments().length > 1) {
            <app-badge variant="secondary" badgeStyle="outline" size="xs" title="Multi-Segment"></app-badge>
          }
        </div>

        <!-- Case 1: Standard Load (No Handoff / Single Segment) -->
        @if (segments().length <= 1) {
          <div class="flex flex-wrap items-center gap-2">
            @if (hasFullDispatchSheet() || load.documents?.latestDispatchSheet) {
            <app-button variant="secondary" size="sm" prefixIcon="Truck"
              (click)="viewFullDispatchSheet()">
              View Dispatch Sheet
            </app-button>
            }
            @if (loadState().showGenerateDispatchButton) {
            <app-button
              variant="primary"
              size="sm"
              [prefixIcon]="(hasFullDispatchSheet() || load.documents?.latestDispatchSheet) ? 'RefreshCw' : 'Plus'"
              [disabled]="isLoading() || !loadState().canGenerateDispatchSheet"
              [loading]="isLoading()"
              (click)="generateDispatchSheet()">
              {{ (hasFullDispatchSheet() || load.documents?.latestDispatchSheet) ? "Regenerate Dispatch Sheet" : "Generate Dispatch Sheet" }}
            </app-button>
            }
          </div>
        }

        <!-- Case 2: Multi-Segment Handoff Load -->
        @if (segments().length > 1) {
          <div class="space-y-2">
            <!-- Full Load Dispatch Sheet Row -->
            <div
              class="flex items-center justify-between p-2.5 rounded-lg bg-surface border border-border"
              animate.enter="hv-item-enter"
              animate.leave="hv-item-leave"
            >
              <div class="flex items-center gap-2 min-w-0">
                <lucide-icon name="FileText" size="16" class="text-primary shrink-0"></lucide-icon>
                <div class="truncate">
                  <app-text size="xs" weight="semibold" class="block truncate">Full Load Dispatch Sheet</app-text>
                  <app-text size="xs" color="muted" class="text-[11px] block">Entire journey (all stops)</app-text>
                </div>
              </div>
              <div class="flex items-center gap-1.5 shrink-0">
                @if (hasFullDispatchSheet()) {
                  <app-button variant="secondary" size="xs" prefixIcon="Eye" (click)="viewFullDispatchSheet()">
                    View
                  </app-button>
                } @else {
                  <app-badge variant="secondary" badgeStyle="outline" size="xs" title="Not Generated"></app-badge>
                }
              </div>
            </div>

            <!-- Segment Dispatch Sheets List -->
            @for (segment of segments(); track segment.id) {
              <div
                class="flex items-center justify-between p-2.5 rounded-lg bg-surface border border-border"
                animate.enter="hv-item-enter"
                animate.leave="hv-item-leave"
                [style.--stagger-idx]="$index"
              >
                <div class="flex items-center gap-2 min-w-0">
                  <lucide-icon name="Truck" size="16" class="text-info shrink-0"></lucide-icon>
                  <div class="truncate">
                    <div class="flex items-center gap-1.5 truncate">
                      <app-text size="xs" weight="semibold" class="truncate">
                        Leg {{ segment.segmentNumber }}: {{ getSegmentDriverDisplay(segment) }}
                      </app-text>
                    </div>
                    <app-text size="xs" color="muted" class="text-[11px] block truncate">
                      Stops {{ segment.fromStopNumber }}–{{ segment.toStopNumber }} · {{ segment.truckUnitNumber || 'Truck' }}
                    </app-text>
                  </div>
                </div>
                <div class="flex items-center gap-1.5 shrink-0">
                  @if (hasSegmentDispatchSheet(segment)) {
                    <app-button variant="secondary" size="xs" prefixIcon="Eye" (click)="viewSegmentDispatchSheet(segment)">
                      View
                    </app-button>
                  } @else {
                    <app-badge variant="secondary" badgeStyle="outline" size="xs" title="Not Generated"></app-badge>
                  }
                </div>
              </div>
            }

            <!-- Dropdown to Generate or Regenerate Any Sheet -->
            @if (loadState().showGenerateDispatchButton && loadState().canGenerateDispatchSheet) {
              <div class="pt-1.5 flex items-center justify-start">
                <app-dropdown
                  variant="primary"
                  size="sm"
                  [triggerLabel]="hasAnyDispatchSheet() ? 'Regenerate Dispatch Sheet' : 'Generate Dispatch Sheet'"
                  [triggerIcon]="hasAnyDispatchSheet() ? 'RefreshCw' : 'Plus'"
                  [disabled]="isLoading() || !loadState().canGenerateDispatchSheet"
                  [loading]="isLoading()"
                  [options]="dispatchSheetOptions()"
                  (selected)="handleDispatchSheetSelection($event)">
                </app-dropdown>
              </div>
            }
          </div>
        }
      </div>
    </div>
  </app-card>

  <!-- Section 3: Driver & Equipment -->
  <app-card
    headerText="Driver & Equipment"
    size="sm"
    icon="Users"
    [editable]="loadState().canEditAssetAssignments && !isMultiLeg()"
    (edit)="openEditModal('drivers', load)"
    editBtnTooltip="Change Driver & Equipment"
    [collapsible]="true"
    [(collapsed)]="driverCollapsed"
  >
    <!-- Collapsed Summary -->
    <div card-summary class="flex justify-between items-center text-sm">
      @if (isMultiLeg()) {
        <div class="flex flex-col">
          <app-text color="muted" size="xs">Relay Assignment</app-text>
          <app-text weight="medium">{{ segments().length }} Legs</app-text>
        </div>
        <div class="flex flex-col items-end">
          <app-text color="muted" size="xs">Current Eq.</app-text>
          <app-text weight="medium">{{ truck() }} / {{ trailer() }}</app-text>
        </div>
      } @else {
        <div class="flex flex-col">
          <app-text color="muted" size="xs">Driver</app-text>
          <app-text weight="medium" class="truncate max-w-[140px]">{{ driver1() }}</app-text>
        </div>
        <div class="flex flex-col items-end">
          <app-text color="muted" size="xs">Eq.</app-text>
          <app-text weight="medium">{{ truck() }} / {{ trailer() }}</app-text>
        </div>
      }
    </div>

    @if (!isMultiLeg()) {
      <div class="space-y-3 text-sm">
        @if (load.team) {
        <div class="flex flex-col pt-2 gap-1.5">
          <app-badge variant="primary" prefixIcon="Users" size="xs" title="Team"></app-badge>
          <div class="flex justify-between">
            <app-text color="muted">Driver 1</app-text>
            <app-text weight="medium">{{ driver1() }}</app-text>
          </div>
          <div class="flex justify-between">
            <app-text color="muted">Driver 2</app-text>
            <app-text weight="medium">{{ driver2() }}</app-text>
          </div>
        </div>
        } @else {
        <div class="flex flex-col pt-2 gap-1.5">
          <app-badge variant="primary" prefixIcon="User" size="xs" title="Solo"></app-badge>
          <div class="flex justify-between">
            <app-text color="muted">Driver</app-text>
            <app-text weight="medium">{{ driver1() }}</app-text>
          </div>
        </div>
        }
      </div>
      <div class="mt-4 grid grid-cols-2 gap-4 text-center border-t border-border pt-3">
        <div>
          <app-text size="xs" color="muted">Truck</app-text>
          <app-text weight="medium">{{ truck() }}</app-text>
        </div>
        <div>
          <app-text size="xs" color="muted">Trailer</app-text>
          <app-text weight="medium">{{ trailer() }}</app-text>
        </div>
      </div>

      @if (loadState().canAddHandoff && (load.stops?.length || 0) >= 2) {
        <div class="mt-3 pt-3 border-t border-border">
          <app-button
            variant="outline"
            size="sm"
            icon="Split"
            class="w-full"
            [disabled]="isLoading()"
            (click)="openSplitRouteModal()"
          >
            Split Route / Relay Handoff
          </app-button>
        </div>
      }
    } @else {
      <!-- Multi-Leg Summary inside Driver & Equipment Card -->
      <div class="flex flex-col gap-2 pt-1">
        <div class="flex items-center justify-between">
          <app-badge variant="info" prefixIcon="GitFork" size="xs" [title]="'Multi-Leg Relay (' + segments().length + ' Legs)'"></app-badge>
        </div>
        @for (seg of segments(); track seg.id) {
          <div class="p-2.5 rounded-lg bg-surface border border-border flex items-center justify-between gap-2">
            <div class="flex flex-col min-w-0">
              <div class="flex items-center gap-1.5 flex-wrap">
                <app-text size="xs" weight="bold" class="text-primary">Leg {{ seg.segmentNumber }}</app-text>
                <app-text size="xs" color="muted">Stops {{ seg.fromStopNumber }}–{{ seg.toStopNumber }}</app-text>
                <app-badge [variant]="getSegmentBadgeVariant(seg)" size="xs" [title]="getSegmentStatusLabel(seg)"></app-badge>
              </div>
              <app-text size="sm" weight="semibold" class="truncate mt-0.5">
                {{ getSegmentDriverDisplay(seg) }}
              </app-text>
              <app-text size="xs" color="muted" class="truncate">
                {{ seg.truckUnitNumber || 'Unassigned' }} / {{ seg.trailerUnitNumber || 'Unassigned' }} &middot; {{ getSegmentMilesDisplay(seg) | number }} mi
              </app-text>
            </div>
            @if ((loadState().canEditAssetAssignments || loadState().canAddHandoff) && !isSegmentSettled(seg)) {
              <app-button
                variant="ghost"
                size="xs"
                [iconOnly]="true"
                icon="Pencil"
                title="Edit Leg Assignment"
                (click)="openEditLegModal(seg)"
              />
            }
          </div>
        }

        @if (loadState().canAddHandoff && (load.stops?.length || 0) >= 2) {
          <div class="pt-1">
            <app-button
              variant="outline"
              size="sm"
              icon="Split"
              class="w-full"
              [disabled]="isLoading()"
              (click)="openSplitRouteModal()"
            >
              Add Another Relay Split
            </app-button>
          </div>
        }
      </div>
    }
  </app-card>

  <!-- Section 3b: Per-Driver / Per-Leg Pay Attribution -->
  @if (perms.canViewPayroll()) {
    <app-load-pay-summary
      [load]="load"
      [segments]="segments()"
      [canEditLegs]="loadState().canEditAssetAssignments || loadState().canAddHandoff"
      (adjustLegPay)="openAdjustLegPayModal($event)"
    />
  }

  <!-- Section 3: Load Details -->
  <app-card headerText="Load Details" size="sm" icon="Package" [editable]="loadState().canEditDetails"
    (edit)="openEditModal('details', load)" [collapsible]="true" [(collapsed)]="detailsCollapsed">
    <!-- Collapsed Summary -->
    <div card-summary class="flex justify-between items-center text-sm">
      <div class="flex flex-col">
        <app-text color="muted" size="xs">Commodity</app-text>
        <app-text weight="medium" class="truncate max-w-[120px]">{{ load.commodity || '--' }}</app-text>
      </div>
      <div class="flex flex-col items-end">
        <app-text color="muted" size="xs">Weight</app-text>
        <app-text weight="medium">{{ load.weightLbs | number }} lbs</app-text>
      </div>
    </div>
    <div class="flex flex-col -mx-4 -mt-4 mb-4">
      @if (load.isTracked) {
      <div class="flex gap-2 py-1 justify-center items-center bg-info-low-contrast">
        <lucide-icon name="Radar" size="16" class="text-info"></lucide-icon>
        <app-text size="base" weight="semibold" color="info">Tracked via
          {{
          load?.trackingApp || "--" | enumLabel : "TrackingAppType"
          }}</app-text>
      </div>
      } @if (load.endorsements?.length) {
      <div class="grid grid-cols-2 gap-4 py-1 text-center bg-warning-low-contrast">
        @for (endorsement of load.endorsements; track endorsement) {
        <div>
          <app-text weight="medium" color="warning">{{
            endorsement | enumLabel : "EndorsementType"
            }}</app-text>
        </div>
        }
      </div>
      }
    </div>
    <div class="grid grid-cols-3 gap-4 text-center border-b border-border pt-1 pb-3 mb-4">
      <div>
        <app-text size="xs" color="muted">Miles</app-text>
        <app-text weight="medium">{{ load.loadedMiles | number }} mi</app-text>
      </div>
      <div>
        <app-text size="xs" color="muted">Weight</app-text>
        <app-text weight="medium">{{ load.weightLbs | number }} lbs</app-text>
      </div>
      <div>
        <app-text size="xs" color="muted">Trailer</app-text>
        <app-text weight="medium">{{
          load.trailerType | enumLabel : "EquipmentType"
          }}</app-text>
      </div>
    </div>
    <div class="space-y-3 text-sm">
      <div class="flex justify-between">
        <app-text color="muted">Commodity</app-text>
        <app-text weight="medium">{{ load.commodity }}</app-text>
      </div>
      @if (temperatureDisplay(); as temp) {
      <div class="flex justify-between">
        <app-text color="muted">Temperature</app-text>
        <app-badge [title]="temp" variant="info" prefixIcon="Snowflake"></app-badge>
      </div>
      }
    </div>
    <!-- Reference Numbers -->
    @if (load.referenceNumbers && load.referenceNumbers.length > 0) {
    <div class="mt-4 grid grid-cols-3 gap-3 text-left border-t border-border pt-3">
      @for (refNum of load.referenceNumbers; track refNum.value) {
      <div class="min-w-0">
        <app-text size="xs" color="muted" class="truncate">{{
          refNum.key | enumLabel : "ReferenceType"
          }}</app-text>
        <app-text size="xs" weight="medium" class="uppercase break-all">{{
          refNum.value
          }}</app-text>
      </div>
      }
    </div>
    }
    <!-- Items count -->
    @if (load.itemCounts && load.itemCounts.length > 0) {
    <div class="mt-4 grid grid-cols-3 gap-3 text-center border-t border-border pt-3">
      @for (itemCount of load.itemCounts; track itemCount.key) {
      <div class="min-w-0">
        <app-text size="xs" color="muted" class="break-words">{{
          itemCount.key | enumLabel : "ItemType"
          }}</app-text>
        <app-text size="xs" weight="medium" class="uppercase break-all">{{
          itemCount.value
          }}</app-text>
      </div>
      }
    </div>
    }
  </app-card>

  <!-- Section 4: Instructions & Notes -->
  <app-card headerText="Instructions & Notes" size="sm" icon="NotebookPen" [editable]="loadState().canEditDetails"
    (edit)="openEditModal('notes', load)" [collapsible]="true" [(collapsed)]="notesCollapsed">
    <!-- Collapsed Summary -->
    <div card-summary class="flex justify-between items-center text-sm">
      <app-text color="muted" size="xs">
        {{ load.instructions || load.notes ? 'Notes available' : 'No notes added' }}
      </app-text>
    </div>
    @if (load.instructions || load.notes) {
    <div class="space-y-3 text-sm">
      @if (load.instructions) {
      <div>
        <app-text size="xs" color="muted">Driver Instructions</app-text>
        <app-text weight="medium">{{ load.instructions }}</app-text>
      </div>
      } @if (load.notes) {
      <div>
        <app-text size="xs" color="muted">Other Notes</app-text>
        <app-text weight="medium">{{ load.notes }}</app-text>
      </div>
      }
    </div>
    } @else {
    <div class="flex items-center justify-center rounded-b-md">
      <app-text color="muted">No instructions or notes added.</app-text>
    </div>
    }
  </app-card>

  <!-- Section 5: Unified Route & Legs Itinerary -->
  <div class="flex flex-col gap-2">
    <div class="card-base card-header-modern rounded-b-none!">
      <div class="icon-wrapper">
        <lucide-icon name="Map" size="14"></lucide-icon>
      </div>
      <app-text size="base" weight="semibold">{{ isMultiLeg() ? 'Route & Legs' : 'Route' }}</app-text>
      <div class="ml-auto flex items-center gap-1.5">
        @if (isMultiLeg()) {
          <app-badge variant="info" badgeStyle="outline" size="xs" [title]="segments().length + ' Legs'"></app-badge>
        }
        <app-badge variant="secondary" [title]="pickCount() + ' Pick | ' + deliveryCount() + ' Drop'"></app-badge>
        @if (loadState().canAddHandoff && (load.stops?.length || 0) >= 2) {
          <app-button
            variant="ghost"
            size="xs"
            icon="Split"
            title="Split route / schedule relay handoff"
            [disabled]="isLoading()"
            (click)="openSplitRouteModal()"
          >
            Split
          </app-button>
        }
        @if (loadState().canEditRoute) {
          <app-button
            variant="ghost"
            size="sm"
            iconColor="text-muted"
            [iconOnly]="true"
            icon="Pencil"
            title="Reorder or remove stops"
            (click)="openEditModal('route', load)"
          />
        }
      </div>
    </div>

    <!-- Single-Leg Mode (90% case: calm, uncluttered stop itinerary) -->
    @if (!isMultiLeg()) {
      @for (stop of load.stops; track $index; let isLast = $last) {
        <app-load-stop-card
          [stop]="stop"
          [canEdit]="loadState().canEditStops"
          [canSplitHere]="canSplitAtStop(stop)"
          [stickyNotesCount]="getStopStickyCount(stop.stopNumber)"
          (edit)="openEditStopModal(load, stop.stopNumber)"
          (splitHere)="openSplitRouteModal(stop.stopNumber!)"
          (openStickyNotes)="openStickyNotesForStop(stop.stopNumber)"
          (viewDocument)="viewDocumentUrl($event.url, $event.title, $event.documentCategory)"
        ></app-load-stop-card>

        @if (!isLast) {
          <div class="py-3 px-2 flex flex-col items-center gap-2.5">
            <div class="w-full flex items-center gap-3 px-4">
              <div class="flex-grow border-t border-dashed border-border"></div>
              <div class="flex items-center gap-1.5 text-xs text-muted">
                <app-badge [title]="(stop?.milesToNext | number) + ' mi'" size="xs" variant="default"></app-badge>
                <span class="text-muted">&bull;</span>
                <app-badge [title]="stop?.durationToNextInSeconds | duration" size="xs" variant="default"></app-badge>
              </div>
              <div class="flex-grow border-t border-dashed border-border"></div>
            </div>

            @if (loadState().canEditStops) {
              <app-button
                variant="secondary"
                size="sm"
                icon="Plus"
                (click)="openAddStopModal(stop.stopNumber!)"
                class="shadow-xs hover:border-primary/50 transition-colors"
              >
                Insert Stop
              </app-button>
            }
          </div>
        } @else if (loadState().canEditStops) {
          <div class="pt-4 pb-2 flex justify-center">
            <app-button
              variant="secondary"
              size="sm"
              icon="Plus"
              (click)="openAddStopModal(stop.stopNumber!)"
              class="shadow-xs hover:border-primary/50 transition-colors"
            >
              Add Stop
            </app-button>
          </div>
        }
      }
    } @else {
      <!-- Multi-Leg Mode: Stops grouped cleanly inside Leg 1, Leg 2... containers -->
      <div class="flex flex-col gap-3">
        @for (group of legGroups(); track group.segment.id) {
          <!-- Relay Handoff Connector between Leg k-1 and Leg k -->
          @if (!group.isFirst && group.previousSegment) {
            <div class="mx-2 p-3 rounded-xl bg-surface-high border border-warning flex flex-col gap-1.5">
              <div class="flex items-center justify-between gap-2 flex-wrap">
                <div class="flex items-center gap-2 min-w-0">
                  <lucide-icon name="ArrowRightLeft" size="15" class="text-warning shrink-0"></lucide-icon>
                  <app-text size="xs" weight="bold" class="text-warning uppercase tracking-wide">
                    Relay Handoff at Stop {{ group.segment.fromStopNumber }}
                  </app-text>
                </div>
                @if (group.canUndoSplit) {
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="Merge"
                    title="Undo this relay split and merge back into previous leg"
                    (click)="openMergeLegModal(group.segment)"
                  >
                    Undo Split
                  </app-button>
                }
              </div>
              <div class="flex items-center gap-1.5 flex-wrap text-xs">
                <app-text size="xs" weight="semibold">{{ getSegmentDriverDisplay(group.previousSegment) }}</app-text>
                <lucide-icon name="ArrowRight" size="12" class="text-muted"></lucide-icon>
                <app-text size="xs" weight="semibold" class="text-primary">{{ group.driverDisplay }}</app-text>
                @if (group.boundaryEntryStop?.address?.city) {
                  <app-text size="xs" color="muted">
                    &middot; {{ group.boundaryEntryStop?.address?.city }}, {{ group.boundaryEntryStop?.address?.state }}
                  </app-text>
                }
              </div>
              @if (group.segment.exchangeReason) {
                <app-text size="xs" color="muted" class="italic">
                  &ldquo;{{ group.segment.exchangeReason }}&rdquo;
                </app-text>
              }
            </div>
          }

          <!-- Leg Container -->
          <div class="rounded-xl border border-border bg-surface p-3 flex flex-col gap-2.5">
            <!-- Leg Header Card -->
            <div class="p-3 rounded-lg bg-card border border-border flex flex-col gap-2">
              <div class="flex items-center justify-between gap-2 flex-wrap">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary-low-contrast text-primary">
                    <lucide-icon name="Route" size="12" class="text-primary"></lucide-icon>
                    <app-text renderAs="span" size="xs" weight="bold" class="text-primary">
                      Leg {{ group.segment.segmentNumber }}
                    </app-text>
                  </span>
                  <app-text size="xs" weight="semibold">
                    Stops {{ group.segment.fromStopNumber }}–{{ group.segment.toStopNumber }}
                  </app-text>
                  <app-text size="xs" color="muted">&middot; {{ group.miles | number }} mi</app-text>
                  <app-badge
                    [variant]="getSegmentBadgeVariant(group.segment)"
                    size="xs"
                    [title]="getSegmentStatusLabel(group.segment)"
                  ></app-badge>
                </div>

                <!-- Inline Leg Actions -->
                <div class="flex items-center gap-1 flex-wrap">
                  @if (hasSegmentDispatchSheet(group.segment)) {
                    <app-button
                      variant="secondary"
                      size="xs"
                      prefixIcon="Eye"
                      (click)="viewSegmentDispatchSheet(group.segment)"
                    >
                      Leg Sheet
                    </app-button>
                    @if (loadState().canGenerateDispatchSheet) {
                      <app-button
                        variant="ghost"
                        size="xs"
                        [iconOnly]="true"
                        icon="RefreshCw"
                        title="Regenerate Leg Dispatch Sheet"
                        [disabled]="isLoading()"
                        [loading]="isLoading()"
                        (click)="generateDispatchSheet(group.segment.id)"
                      />
                    }
                  } @else if (loadState().canGenerateDispatchSheet) {
                    <app-button
                      variant="outline"
                      size="xs"
                      prefixIcon="FileText"
                      [disabled]="isLoading()"
                      [loading]="isLoading()"
                      (click)="generateDispatchSheet(group.segment.id)"
                    >
                      Gen Sheet
                    </app-button>
                  }

                  @if ((loadState().canEditAssetAssignments || loadState().canAddHandoff) && !isSegmentSettled(group.segment)) {
                    <app-button
                      variant="ghost"
                      size="xs"
                      icon="Pencil"
                      title="Edit Leg Driver & Equipment"
                      (click)="openEditLegModal(group.segment)"
                    >
                      Edit
                    </app-button>
                  }

                  @if (group.canUndoSplit) {
                    <app-button
                      variant="ghost"
                      size="xs"
                      [iconOnly]="true"
                      icon="Merge"
                      iconColor="text-muted"
                      title="Undo Split / Merge into Previous Leg"
                      (click)="openMergeLegModal(group.segment)"
                    />
                  }
                </div>
              </div>

              <!-- Driver & Equipment Summary Row -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-border text-xs">
                <div class="flex items-center gap-1.5 min-w-0">
                  <lucide-icon [name]="group.segment.driver2Id ? 'Users' : 'User'" size="14" class="text-primary shrink-0"></lucide-icon>
                  <app-text size="xs" weight="semibold" class="truncate">{{ group.driverDisplay }}</app-text>
                </div>
                <div class="flex items-center gap-1.5 min-w-0 sm:justify-end">
                  <lucide-icon name="Truck" size="14" class="text-primary shrink-0"></lucide-icon>
                  <app-text size="xs" weight="medium" class="truncate">
                    {{ group.segment.truckUnitNumber || 'Unassigned' }} / {{ group.segment.trailerUnitNumber || 'Unassigned' }}
                  </app-text>
                </div>
              </div>
            </div>

            <!-- Outbound transit leg from shared boundary entry stop (for Leg 2+) -->
            @if (group.boundaryEntryStop; as entryStop) {
              @if (group.exclusiveStops.length > 0) {
                <div class="py-2 px-2 flex flex-col items-center gap-2">
                  <div class="w-full flex items-center gap-3 px-4">
                    <div class="flex-grow border-t border-dashed border-border"></div>
                    <div class="flex items-center gap-1.5 text-xs text-muted">
                      <app-badge [title]="(entryStop?.milesToNext | number) + ' mi'" size="xs" variant="default"></app-badge>
                      <span class="text-muted">&bull;</span>
                      <app-badge [title]="entryStop?.durationToNextInSeconds | duration" size="xs" variant="default"></app-badge>
                    </div>
                    <div class="flex-grow border-t border-dashed border-border"></div>
                  </div>

                  @if (loadState().canEditStops) {
                    <app-button
                      variant="secondary"
                      size="sm"
                      icon="Plus"
                      (click)="openAddStopModal(entryStop.stopNumber!)"
                      class="shadow-xs hover:border-primary/50 transition-colors"
                    >
                      Insert Stop
                    </app-button>
                  }
                </div>
              }
            }

            <!-- Stop Cards within this Leg -->
            @for (stop of group.exclusiveStops; track stop.stopNumber; let isLastInLeg = $last) {
              <app-load-stop-card
                [stop]="stop"
                [canEdit]="loadState().canEditStops"
                [canSplitHere]="canSplitAtStop(stop)"
                [servicedByLabel]="getStopServicedByLabel(stop)"
                [stickyNotesCount]="getStopStickyCount(stop.stopNumber)"
                (edit)="openEditStopModal(load, stop.stopNumber)"
                (splitHere)="openSplitRouteModal(stop.stopNumber!)"
                (openStickyNotes)="openStickyNotesForStop(stop.stopNumber)"
                (viewDocument)="viewDocumentUrl($event.url, $event.title, $event.documentCategory)"
              ></app-load-stop-card>

              @if (!isLastInLeg) {
                <div class="py-2 px-2 flex flex-col items-center gap-2">
                  <div class="w-full flex items-center gap-3 px-4">
                    <div class="flex-grow border-t border-dashed border-border"></div>
                    <div class="flex items-center gap-1.5 text-xs text-muted">
                      <app-badge [title]="(stop?.milesToNext | number) + ' mi'" size="xs" variant="default"></app-badge>
                      <span class="text-muted">&bull;</span>
                      <app-badge [title]="stop?.durationToNextInSeconds | duration" size="xs" variant="default"></app-badge>
                    </div>
                    <div class="flex-grow border-t border-dashed border-border"></div>
                  </div>

                  @if (loadState().canEditStops) {
                    <app-button
                      variant="secondary"
                      size="sm"
                      icon="Plus"
                      (click)="openAddStopModal(stop.stopNumber!)"
                      class="shadow-xs hover:border-primary/50 transition-colors"
                    >
                      Insert Stop
                    </app-button>
                  }
                </div>
              }
            }

            @if (group.isLast && loadState().canEditStops && load.stops.length > 0) {
              <div class="pt-2 pb-1 flex justify-center">
                <app-button
                  variant="secondary"
                  size="sm"
                  icon="Plus"
                  (click)="openAddStopModal(load.stops[load.stops.length - 1].stopNumber!)"
                  class="shadow-xs hover:border-primary/50 transition-colors"
                >
                  Add Stop
                </app-button>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>

  <!-- Section: Audit Log (Restricted to Root, Admin, and Manager roles) -->
  @if (canViewAuditLog()) {
    <app-audit-log-timeline [loadId]="load.id" [(collapsed)]="auditCollapsed" />
  }

  <!-- Cancel/Revive load -->
  @if (load.status !== 'CANCELLED') {
  <div class="flex flex-col mt-2">
    <app-button variant="danger" size="sm" prefixIcon="XCircle" (click)="cancelLoad()" [loading]="isLoading()"
      [disabled]="isLoading()">
      Cancel Load
    </app-button>
  </div>
  } @else {
  <div class="flex flex-col mt-2">
    <app-button variant="info" size="sm" prefixIcon="RefreshCcw" (click)="reviveLoad()" [loading]="isLoading()"
      [disabled]="isLoading()">
      Revive Load
    </app-button>
  </div>
  }
</div>
} @else {
<!-- Empty state when no load is selected or load is null -->
<div class="h-full flex flex-col items-center justify-center p-8 text-center">
  <div class="w-16 h-16 rounded-full bg-surface border border-border flex items-center justify-center mb-4">
    <lucide-icon name="Package" class="w-8 h-8 text-muted"></lucide-icon>
  </div>
  <app-text weight="medium" class="mb-1">No load selected</app-text>
  <app-text variant="muted" size="sm">Select a load from the list to view its details here.</app-text>
</div>
}